import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import type { AllocatableSite } from '@/types/api/allocatable-sites.types';
import type {
  ItemDrawBreakdown,
  ProjectBillItem,
  ProjectCatalogueItem,
} from '@/types/api/project-items.types';
import type { BulkOrderPlacementAllocation } from '@/types/api/bulk-order-placement.types';
import type {
  BulkOrderPreviewAllocationRequest,
  BulkOrderPreviewCell,
  BulkOrderPreviewResponse,
} from '@/types/api/bulk-order-preview.types';
import type { BoqLineSet } from '@/types/api/boq-lines.types';
import {
  bindingQuantity,
  isAllocated,
  previewCacheKey,
  previewCellKey,
  rowFiguresFor,
  type AllocationBinding,
  type AllocationRowFigures,
} from '@/common/utils/bulk-order-allocation.utils';
import {
  emptyVendorPayment,
  paymentProblems,
  toPlacementPayment,
  type BulkOrderVendorPayment,
} from '@/common/utils/bulk-order-payment.utils';
import type { BulkOrderPlacementPayment } from '@/types/api/bulk-order-placement.types';
import { clearBulkOrderWizardSession } from '@/composables/use-bulk-order-wizard-session';
import { resultingOrderKind, type ResultingOrderKind } from '@/common/utils/bulk-orders.utils';
import type { StagedOrderDocument } from '@/types/api/orders.types';

/** The five steps, in order. The index is what the store holds and the side-car persists. */
export const BULK_ORDER_WIZARD_STEPS = ['items', 'sites', 'allocate', 'payment', 'review'] as const;

export type BulkOrderWizardStepName = (typeof BULK_ORDER_WIZARD_STEPS)[number];

/**
 * Every step's route name, derived rather than listed, for the layouts that special-case the
 * wizard. Three hand-written copies of this list each missed Payment and delivery when it became the
 * fifth step (PE-990), so a new step now reaches them by being added to the steps above.
 */
export const BULK_ORDER_WIZARD_ROUTE_NAMES: readonly string[] = BULK_ORDER_WIZARD_STEPS.map(
  (step) => `create-bulk-order-${step}`,
);

export const BILL_ITEMS_LOADER = 'bulk-order-wizard-bill-items';
export const CATALOGUE_ITEMS_LOADER = 'bulk-order-wizard-catalogue-items';
export const ALLOCATABLE_SITES_LOADER = 'bulk-order-wizard-allocatable-sites';

/**
 * An item the user has added, flattened to what every later step needs.
 *
 * The vendor is carried on the selection rather than looked up, because the vendor is
 * *derived* from the items chosen (FR-6) and a selection spanning vendors splits into one bulk
 * order per vendor at Review (FR-3). `isOffBill` is the single fact the variation flag renders
 * from — an off-bill item has no BOQ line, so it has no coverage or draw standing either.
 */
export interface SelectedBulkOrderItem {
  id: string;
  name: string;
  vendorId: string;
  vendorName: string;
  type?: string | null;
  unitType?: string | null;
  unitTypeShortName?: string | null;
  rate?: number | null;
  /** Present for on-bill items only. */
  siteCount?: number;
  isOffBill: boolean;
}

/** One page of a bill-item search, cached under the query string that produced it. */
interface BillItemsPage {
  items: ProjectBillItem[];
  totalCount: number;
  eligibleSiteCount: number;
  drawDataAvailable: boolean;
  drawBreakdowns: ItemDrawBreakdown[];
}

/** One page of a catalogue search, cached under the query string that produced it. */
interface CatalogueItemsPage {
  items: ProjectCatalogueItem[];
  totalCount: number;
}

/**
 * One allocatable-sites answer: the **whole** site set plus the envelope that describes it.
 *
 * Context: not a page — the read is unpaginated (DL-38), so an entry is the complete list. It is
 * keyed on the project *and the selected item ids*, because coverage and draw standing are both
 * measured against that selection; a key of project alone would serve the previous item set's
 * coverage after a Back-and-edit.
 */
interface AllocatableSitesEntry {
  sites: AllocatableSite[];
  eligibleSiteCount: number;
  drawDataAvailable: boolean;
}

/**
 * The Allocate grid: one item, its sites, and each site's bindings to that site's own BOQ lines.
 *
 * Context: three levels rather than two because a site legitimately binds one item to **several**
 * of its lines, which is the whole reason the table nests (FR-9, DL-20). The earlier
 * `Record<itemId, Record<siteId, number>>` had nowhere to put the second line, and nowhere to put
 * the binding itself.
 */
export type AllocationGrid = Record<string, Record<string, AllocationBinding[]>>;

/** The rollup one item's tile, header and completion bar all render from. */
export interface ItemAllocationSummary {
  /** Sites this item allocates to — its rows, not the step-2 selection. */
  siteCount: number;
  /** How many of them carry a quantity. The completion bar's "N of M sites" figure. */
  allocatedSiteCount: number;
  totalQuantity: number;
  total: number;
  /** Sites raising at least one **gating** variation. The loud line (DL-52). */
  gatingSiteCount: number;
  /** Sites raising variations, none of which gate. The quiet line. */
  nonGatingSiteCount: number;
}

/**
 * In-session state for the create-bulk-order wizard.
 *
 * **Deliberately not registered with `pinia-plugin-persistedstate`** (DL-93, DL-94). There is no
 * backend draft and no `Draft` lifecycle state in v1 — that was costed and declined, because every
 * read path would then have to exclude it. What the wizard gets instead is a `sessionStorage`
 * side-car owned by the **view**, which dies with the tab: see
 * `use-bulk-order-wizard-session.ts`. It is not save-and-resume, and exit still needs its confirm.
 *
 * Two things this store deliberately does **not** do:
 *
 * - **It derives no money and no variation verdict.** No getter here turns its own state into a
 *   total, a remaining budget or a variation outcome. Financial logic is server-side without
 *   exception; the store caches the last successful `POST /bulk-orders/preview` response and a
 *   pending flag, and components render from that response. The endpoint itself is batch A.3a and
 *   its wiring A.3b — `lastPreview` is the seam, held here so those batches extend rather than
 *   restructure.
 * - **It holds no paging, filter or search state.** Those are transient and belong to the
 *   component that renders them (PE-671).
 */
/**
 * One binding as **placement** takes it, or null where it has nowhere to land.
 *
 * 🔴 **The section travels.** This used to map a proposed line down to `{ name, specification }`,
 * which is preview's shape — so the section the new-line form collects and the binding holds was
 * dropped here, and every placement carrying a new line was refused with *"needs the name of the
 * section to hold it"* (review, 2026-09-22). Placement creates or matches that section by name
 * inside the site's own transaction (ADR-B13), so it is the one contract that needs it.
 *
 * `subSectionName` stays `null` rather than `''` where there is none: absent means "directly under
 * the section" and an empty string is refused as an unnamed sub-section.
 */
const requestRowFor = (
  itemId: string,
  siteId: string,
  binding: AllocationBinding,
): BulkOrderPlacementAllocation | null => {
  if (!isAllocated(binding)) return null;
  if (binding.boqLineItemId == null && binding.proposedLine == null) return null;

  return {
    siteId,
    itemId,
    boqLineItemId: binding.proposedLine ? null : binding.boqLineItemId,
    proposedLine: binding.proposedLine
      ? {
          name: binding.proposedLine.name,
          specification: binding.proposedLine.specification,
          sectionName: binding.proposedLine.sectionName,
          subSectionName: binding.proposedLine.subSectionName,
        }
      : null,
    quantity: bindingQuantity(binding),
  };
};

/**
 * The same row narrowed to what **preview** takes.
 *
 * ⚠️ Preview is stateless and its proposed line is `{ name, specification }`. Sending it a section
 * would not fail — the server ignores what it does not model — but the two contracts genuinely
 * differ, and a client that blurs them is how the section went missing from the one that needs it.
 */
const previewRowFrom = (row: BulkOrderPlacementAllocation): BulkOrderPreviewAllocationRequest => ({
  siteId: row.siteId,
  itemId: row.itemId,
  boqLineItemId: row.boqLineItemId,
  proposedLine: row.proposedLine
    ? { name: row.proposedLine.name, specification: row.proposedLine.specification }
    : null,
  quantity: row.quantity,
});

export const useBulkOrderWizardStore = defineStore('bulk-order-wizard', () => {
  /** Zero-based index of the step being shown. Mirrors the routed child. */
  const currentStep = ref(0);

  /**
   * Furthest step reached. Only ever grows — that monotonicity is what lets the Stepper show
   * progress while forward navigation stays gated on each step's own readiness.
   */
  const maxReachedStep = ref(0);

  const selectedProjectId = ref<string | null>(null);
  const selectedProjectName = ref<string | null>(null);

  const selectedItems = ref<SelectedBulkOrderItem[]>([]);

  /**
   * The sites the order spans. The Sites step writes it; the shell's step-2 gate reads it.
   *
   * Held here rather than in the step so it survives Back and Next, and so a chip change that
   * hides a selected row cannot lose it — narrowing is a view concern and never touches this.
   */
  const selectedSiteIds = ref<string[]>([]);

  /**
   * The `(project, item set)` the coverage-based pre-selection last ran for, or null.
   *
   * 🔴 **Keyed rather than a one-shot flag, and the difference is a real defect it fixes.** A
   * boolean made pre-selection a once-per-project event, so going back to Items, changing the
   * selection and returning left the old suggestion standing — against coverage that had since
   * been recomputed for different items (CPTO, 2026-09-11). The key re-seeds exactly when the
   * answer changes and stays put when it has not, which is what keeps a return from Allocate from
   * wiping the user's own edits.
   */
  const seededSitesKey = ref<string | null>(null);

  /**
   * Whether the user has touched the site selection themselves.
   *
   * Context: **latched, not compared.** The info message explaining the pre-selection has to go
   * the moment the officer edits anything and stay gone even if they undo it back to the suggested
   * set (CPTO, 2026-09-11) — a banner that reappears because you happened to re-tick the last box
   * is describing state rather than telling you something. Cleared when the seed runs again, since
   * that is a fresh suggestion the message is true about.
   */
  const hasEditedSiteSelection = ref(false);

  /** The allocation grid and the per-item rate overrides — one rate per item (FR-12). */
  const allocationGrid = ref<AllocationGrid>({});
  const rateOverrides = ref<Record<string, number>>({});

  /** Items the officer has marked done, and which item's card is open. */
  const doneItemIds = ref<string[]>([]);
  const activeItemId = ref<string | null>(null);

  /**
   * The grid's **reference basis**: every preview cell held by `(site, item, line)`.
   *
   * 🔴 This is what DL-130 bought. Preview is called about **twice per bulk order** — entering
   * Allocate for this basis, entering Review for the authoritative figures — and between them the
   * store does the display arithmetic over the thresholds these cells supply. Per-keystroke was
   * measured at roughly 120 GB of MongoDB reads for one 100-site order.
   *
   * Cells arrive for every binding the grid **could** use, not only those it has (`candidate` at
   * zero quantity), which is what lets a site show its remaining budget and outstanding quantity
   * before anything is typed. Without that flag the entry call would return nothing.
   */
  const previewCells = ref<Record<string, BulkOrderPreviewCell>>({});

  /**
   * The rate tolerance the server published, and the one rule this client mirrors.
   *
   * Context: read from the response rather than typed in here, because a constant in two places is
   * a constant that drifts. Zero until the first response lands, which raises no variation — the
   * honest reading of "no basis yet" rather than a guess at the platform's value.
   */
  const rateTolerance = ref(0);

  /**
   * Which `(siteId, itemId)` pairs the basis already covers (DL-130's cache key).
   *
   * Context: this is what makes adding a site a **delta** rather than a refetch — the request asks
   * only about pairs not held, and going Back to add an item fetches that item for the sites
   * already covered. ⚠️ It saves round trips and bytes, not database reads: one more item still
   * re-reads every site's bill server-side, which is PE-708's problem.
   */
  const previewedPairs = ref<Record<string, boolean>>({});

  /**
   * The last **authoritative** response: the call over the full grid, made on entering Payment and
   * delivery (DL-144), and made by Review itself when it is entered without one (PE-981).
   *
   * Held so DL-132's stale-figure diff has both snapshots. Entirely client-side: no backend field,
   * no timestamp, nothing persisted.
   *
   * 🔴 **Dropped whenever the wizard goes back before step 4** (see `goToStep`). That is what keeps
   * Review honest: arriving without one, from a stepper jump, a reload or a duplicated tab, it
   * re-prices the grid before showing a figure, so nothing on it is computed from inputs that
   * have since changed (DL-189 P-04).
   */
  const lastAuthoritativePreview = ref<BulkOrderPreviewResponse | null>(null);

  /**
   * The kind of order (bulk or standard) each vendor's payment details were confirmed for on
   * step 4, persisted in the session side-car.
   *
   * Context: Review can now be reached without passing step 4 again (PE-981), so something has to
   * notice when a grid change turned a vendor's order from bulk to standard. The two take different
   * assignees (DL-141), so a stored `ORG_FO` would be refused at placement. A kind that no longer
   * matches makes `isPaymentComplete` false, and Review sends the officer back to step 4.
   */
  const confirmedOrderKinds = ref<Record<string, ResultingOrderKind>>({});

  /** Called by step 4 once it has priced the grid: these are the orders its payments are for. */
  const confirmOrderKinds = (response: BulkOrderPreviewResponse): void => {
    confirmedOrderKinds.value = Object.fromEntries(
      response.vendors.map((vendor) => [vendor.vendorId, resultingOrderKind(vendor.siteCount)]),
    );
  };

  /**
   * True only while one of the two calls is in flight — **never while typing**.
   *
   * Context: that is the whole of DL-130's user-facing win. A figure is *pending* while its basis
   * is being fetched, *zero* when it really is zero, and *not allocated* when the field is blank;
   * the three must render apart, and a recalculating figure must never look like a zero one.
   */
  const previewPending = ref(false);

  /**
   * Sites whose reference data never arrived, so their rows are **paused** rather than pending.
   *
   * Context: pending means a figure is being recalculated and will land; paused means nothing
   * arrived and nothing can be entered until a retry succeeds. Every site whose data *did* arrive
   * stays allocatable, which is why this is a list rather than a step-wide failure.
   */
  const pausedSiteIds = ref<string[]>([]);

  /**
   * Item-search results, cached per query string for the session.
   *
   * There is no HTTP caching layer in this app (PE-616 forbids one), so the step caches
   * its own reads — paging back to a page already seen is common. Keyed by the whole query, so a
   * different filter is a different entry rather than a stale hit, and dropped wholesale on a
   * project change because every row is scoped to the project's bills.
   */
  const billItemsCache = ref<Record<string, BillItemsPage>>({});
  const catalogueItemsCache = ref<Record<string, CatalogueItemsPage>>({});

  /**
   * Allocatable-sites answers, cached per `(projectId, selected item ids)` for the session.
   *
   * Stable within a wizard session (FE-SDD §6), so stepping back from Allocate re-renders the same
   * list without a round trip — which matters more here than on a paged read, because this one
   * returns the whole project.
   */
  const allocatableSitesCache = ref<Record<string, AllocatableSitesEntry>>({});

  /**
   * A site's projected BOQ lines, cached per **`siteId`** and read when a picker opens.
   *
   * Context: held here rather than in the picker's own composable, and the difference is a real
   * defect it avoids. A module-level cache in the composable outlives the wizard — so a second bulk
   * order in the same tab, or a project change, would serve the previous project's bills. Every
   * other wizard cache lives on this store precisely so `$reset` and `setProject` clear it, which
   * is the same reasoning `setProject` already records for the bill-item pages.
   *
   * Keyed on the site alone rather than `(siteId, itemId)`: the read returns the site's whole line
   * set, and narrowing it to an item is a client-side filter — so a second item's picker at the
   * same site is free.
   */
  const siteBoqLinesCache = ref<Record<string, BoqLineSet>>({});

  const selectedItemIds = computed(() => selectedItems.value.map((item) => item.id));

  /**
   * The vendors the selection spans, in the order they were first added.
   *
   * Context: a count, not money — this is what the multi-vendor split note states and what
   * decides how many bulk orders Review will create (FR-3). Insertion order rather than
   * alphabetical so the tray's "Bulk order 1 / 2" numbering is stable as items are added.
   */
  const selectedVendors = computed(() => {
    const seen = new Map<string, string>();

    for (const item of selectedItems.value) {
      if (!seen.has(item.vendorId)) seen.set(item.vendorId, item.vendorName);
    }

    return [...seen].map(([id, name]) => ({ id, name }));
  });

  const isItemSelected = (itemId: string): boolean =>
    selectedItems.value.some((item) => item.id === itemId);

  const addItem = (item: SelectedBulkOrderItem): void => {
    if (isItemSelected(item.id)) return;

    selectedItems.value = [...selectedItems.value, item];
  };

  const removeItem = (itemId: string): void => {
    selectedItems.value = selectedItems.value.filter((item) => item.id !== itemId);
  };

  const clearItems = (): void => {
    selectedItems.value = [];
  };

  const isSiteSelected = (siteId: string): boolean => selectedSiteIds.value.includes(siteId);

  const toggleSite = (siteId: string): void => {
    selectedSiteIds.value = isSiteSelected(siteId)
      ? selectedSiteIds.value.filter((id) => id !== siteId)
      : [...selectedSiteIds.value, siteId];
  };

  /**
   * Replaces the selection wholesale — select-all over the visible rows, and the pre-selection
   * seed.
   *
   * De-duplicated because select-all unions the visible ids onto a selection that may already
   * hold some of them, and a site counted twice would inflate the running count and the two-site
   * gate alike.
   */
  const setSelectedSiteIds = (siteIds: string[]): void => {
    selectedSiteIds.value = [...new Set(siteIds)];
  };

  const clearSites = (): void => {
    selectedSiteIds.value = [];
  };

  // ─── What a removal would throw away (PE-989) ───────────────────────────────
  //
  // Context: removing an item or a site used to drop the Allocate work that depended on it
  // silently. The steps ask first, but only when something would genuinely be lost: removing a
  // thing nothing depends on yet stays instant. These readers answer that question; the discard
  // actions run only once the officer has confirmed.

  /** A row carries work once it has a quantity or points at a line. A fresh seed carries none. */
  const isBindingFilled = (binding: AllocationBinding): boolean =>
    (binding.quantity ?? 0) > 0 || !!binding.boqLineItemId || !!binding.proposedLine;

  const siteHasWork = (itemId: string, siteId: string): boolean =>
    (allocationGrid.value[itemId]?.[siteId] ?? []).some(isBindingFilled);

  /**
   * What removing one item would clear: the sites holding its quantities or lines, and whether its
   * vendor's payment details go with it (they do when it is that vendor's last item).
   */
  const vendorLeftBy = (itemId: string): string | null => {
    const vendorId = selectedItems.value.find((item) => item.id === itemId)?.vendorId;
    const isVendorsLastItem =
      !!vendorId && selectedItems.value.filter((item) => item.vendorId === vendorId).length === 1;

    return isVendorsLastItem ? vendorId : null;
  };

  const itemProgress = (itemId: string) => {
    const siteIds = Object.keys(allocationGrid.value[itemId] ?? {}).filter((siteId) =>
      siteHasWork(itemId, siteId),
    );
    const vendorId = vendorLeftBy(itemId);
    const payment = vendorId ? payments.value[vendorId] : undefined;
    const losesPayment =
      !!vendorId &&
      (!!payment?.method || !!payment?.assigneeId || documentsFor(vendorId).length > 0);

    const isDone = doneItemIds.value.includes(itemId);
    const hasRateOverride = rateOverrides.value[itemId] !== undefined;

    return {
      siteCount: siteIds.length,
      paymentVendorName: losesPayment
        ? (selectedVendors.value.find((vendor) => vendor.id === vendorId)?.name ?? null)
        : null,
      hasProgress: siteIds.length > 0 || isDone || hasRateOverride || losesPayment,
    };
  };

  /** What deselecting these sites would clear: how many items hold work at them. */
  const sitesProgress = (siteIds: string[]) => {
    const itemIds = selectedItems.value
      .map((item) => item.id)
      .filter((itemId) => siteIds.some((siteId) => siteHasWork(itemId, siteId)));

    return { itemCount: itemIds.length, hasProgress: itemIds.length > 0 };
  };

  /**
   * Removes an item and everything Allocate held for it, so re-adding it starts clean. Its vendor's
   * step 4 details go too when it was that vendor's last item, as the confirm dialog says.
   */
  const discardItem = (itemId: string): void => {
    const vendorId = vendorLeftBy(itemId);
    const { [itemId]: _grid, ...grid } = allocationGrid.value;
    const { [itemId]: _rate, ...rates } = rateOverrides.value;

    allocationGrid.value = grid;
    rateOverrides.value = rates;
    doneItemIds.value = doneItemIds.value.filter((id) => id !== itemId);
    if (activeItemId.value === itemId) activeItemId.value = null;
    if (vendorId) {
      const { [vendorId]: _payment, ...restPayments } = payments.value;
      const { [vendorId]: _documents, ...restDocuments } = documents.value;
      const { [vendorId]: _kind, ...restKinds } = confirmedOrderKinds.value;

      payments.value = restPayments;
      documents.value = restDocuments;
      confirmedOrderKinds.value = restKinds;
    }
    removeItem(itemId);
  };

  /**
   * Deselects sites and drops their rows from every item. An item keeps its (now smaller) entry,
   * as `reconcileAllocation` leaves it, so the seed does not hand those sites straight back.
   */
  const discardSites = (siteIds: string[]): void => {
    const dropped = new Set(siteIds);

    allocationGrid.value = Object.fromEntries(
      Object.entries(allocationGrid.value).map(([itemId, bySite]) => [
        itemId,
        Object.fromEntries(Object.entries(bySite).filter(([siteId]) => !dropped.has(siteId))),
      ]),
    );
    selectedSiteIds.value = selectedSiteIds.value.filter((id) => !dropped.has(id));
  };

  /**
   * Records which item set the selection answers. `keepEdits` is for a selection the officer edited:
   * it keeps their mark, so the next item change leaves it alone as well (DL-189 P-07).
   */
  const markSitesSeeded = (key: string, { keepEdits = false } = {}): void => {
    seededSitesKey.value = key;
    if (!keepEdits) hasEditedSiteSelection.value = false;
  };

  /** Called by the step's own user handlers only — never by the seed or the reconcile. */
  const markSitesEdited = (): void => {
    hasEditedSiteSelection.value = true;
  };

  const cacheAllocatableSites = (key: string, entry: AllocatableSitesEntry): void => {
    allocatableSitesCache.value = { ...allocatableSitesCache.value, [key]: entry };
  };

  const cacheSiteBoqLines = (siteId: string, lineSet: BoqLineSet): void => {
    siteBoqLinesCache.value = { ...siteBoqLinesCache.value, [siteId]: lineSet };
  };

  /**
   * Switches the scoped project, discarding everything scoped to the old one.
   *
   * Context: the caller raises the confirm *before* this runs when the tray is non-empty — the
   * loss is stated before it happens, because there is no draft to fall back on. Clearing the
   * caches is not an optimisation: a cached page keyed on the same query string would otherwise
   * serve the previous project's bill.
   */
  const setProject = (projectId: string | null, projectName: string | null): void => {
    if (projectId === selectedProjectId.value) return;

    selectedProjectId.value = projectId;
    selectedProjectName.value = projectName;
    selectedItems.value = [];
    selectedSiteIds.value = [];
    seededSitesKey.value = null;
    hasEditedSiteSelection.value = false;
    allocationGrid.value = {};
    rateOverrides.value = {};
    doneItemIds.value = [];
    activeItemId.value = null;
    previewCells.value = {};
    previewedPairs.value = {};
    rateTolerance.value = 0;
    lastAuthoritativePreview.value = null;
    confirmedOrderKinds.value = {};
    pausedSiteIds.value = [];
    deliveryDate.value = null;
    payments.value = {};
    documents.value = {};
    billItemsCache.value = {};
    catalogueItemsCache.value = {};
    allocatableSitesCache.value = {};
    siteBoqLinesCache.value = {};
  };

  /**
   * When the vendor is to deliver — one date for the whole placement.
   *
   * 🔴 **An order-level fact the wizard never captured.** `BulkOrderPlacementRequestDto` requires it
   * and nothing on this flow supplied one, so every placement was refused (review, 2026-09-22). One
   * date rather than one per site, because a bulk order is one order the officer places once; a
   * per-site date is a different feature.
   *
   * Context: it lives on the Payment and delivery step (S-11) rather than on Review, so that Review
   * is a pure summary (DL-56). The name is unchanged from when Review owned it, which is what keeps
   * the whole submit path in `ReviewStep.place()` reading one field it never had to learn about.
   *
   * ⚠️ **Now inside the session snapshot, and that reverses the note this carried** (PE-884). It
   * was excluded because adding a field meant bumping `SNAPSHOT_VERSION` and discarding every
   * in-flight wizard — a real cost when the date sat on Review, where a restored session was
   * already standing on the control. It gates entry to Review now, so an unpersisted date would
   * bounce every reload past step 4 backwards. The bump is owed anyway for the step indices, so
   * the field costs nothing extra, and the stale-date worry it named is handled where it belongs:
   * the shell drops a restored date that is no longer `minDate` or later.
   */
  const deliveryDate = ref<Date | null>(null);

  const setDeliveryDate = (date: Date | null): void => {
    deliveryDate.value = date;
  };

  /**
   * How each resulting order is paid, keyed by vendor (PE-882).
   *
   * Context: persisted in the session side-car for the reason the delivery date is: it gates
   * Review, and a gate over an unpersisted field bounces every reload backwards (DL-143 G-06).
   * An entry for a vendor no longer in the grid is harmless here and is never sent: placement
   * builds `paymentInformation` from the authoritative response's vendors alone.
   */
  const payments = ref<Record<string, BulkOrderVendorPayment>>({});

  const paymentFor = (vendorId: string): BulkOrderVendorPayment =>
    payments.value[vendorId] ?? emptyVendorPayment();

  /** Replaced rather than mutated, so the side-car's deep watch sees one write per change. */
  const setPayment = (vendorId: string, payment: BulkOrderVendorPayment): void => {
    payments.value = { ...payments.value, [vendorId]: payment };
  };

  /**
   * The documents staged against each resulting order on step 4, keyed by vendor, filed once
   * placement returns the orders' ids.
   *
   * Context: held in memory only. A `File` does not survive JSON, so the session side-car cannot
   * carry these, and a reload drops them while the rest of step 4 comes back. The standard create
   * flow holds its staged documents the same way, on its in-memory form.
   */
  const documents = ref<Record<string, StagedOrderDocument[]>>({});

  const documentsFor = (vendorId: string): StagedOrderDocument[] => documents.value[vendorId] ?? [];

  const stageDocument = (vendorId: string, document: StagedOrderDocument): void => {
    documents.value = { ...documents.value, [vendorId]: [...documentsFor(vendorId), document] };
  };

  const unstageDocument = (vendorId: string, document: StagedOrderDocument): void => {
    documents.value = {
      ...documents.value,
      [vendorId]: documentsFor(vendorId).filter(
        (staged) =>
          !(staged.documentType === document.documentType && staged.file === document.file),
      ),
    };
  };

  /**
   * Every resulting order in the authoritative response has what placement needs, and is the kind
   * of order its payment details were confirmed for.
   */
  const isPaymentComplete = computed(() => {
    const vendors = lastAuthoritativePreview.value?.vendors;

    if (!vendors || vendors.length === 0) return false;

    return vendors.every(
      (vendor) =>
        paymentProblems(payments.value[vendor.vendorId], vendor.totalInclVat).length === 0 &&
        confirmedOrderKinds.value[vendor.vendorId] === resultingOrderKind(vendor.siteCount),
    );
  });

  /** `paymentInformation` for the placement request, one entry per vendor the server will group. */
  const paymentInformationRows = computed<BulkOrderPlacementPayment[]>(() =>
    (lastAuthoritativePreview.value?.vendors ?? []).map((vendor) =>
      toPlacementPayment(vendor.vendorId, paymentFor(vendor.vendorId), vendor.totalInclVat),
    ),
  );

  /**
   * The furthest step the wizard's **current state** entitles it to, recomputed rather than
   * remembered.
   *
   * Context: a high-water mark is not a gate. Reaching Sites, going Back, emptying the tray and
   * pressing Forward re-entered Sites on a selection that no longer qualifies, because history
   * said it had been reached once — the Next button was correctly disabled while the URL walked
   * straight past it. Derived here rather than in the shell because it is a rule about the
   * wizard's data, and the steps that enforce it forward (`ItemsStep`, `SitesStep`) read the same
   * two fields; the steps still own the *reasons*, which is what the footer renders.
   *
   * Allocate's own gate — every item marked done — now bounds Review (A.3b), which is what the
   * note here previously said was owed. So a pasted `/review` URL on a half-allocated grid lands
   * back on Allocate instead of on a Review step reading zeros.
   *
   * 🔴 **Review is gated on the delivery date** (PE-884 criterion 12). Without the clause a pasted
   * `/review` reaches the last step with no date, where the only thing standing between the officer
   * and a dead Place button is a silent `return`. The date is persisted for exactly this reason —
   * a gate over an unpersisted field bounces every reload backwards instead.
   *
   * 🔴 **And on payment** (PE-882). An ungated Review would place with no payment information,
   * which is the PE-879 defect this whole capability exists to fix.
   *
   * 🔴 **Without a priced response, having been there is enough to be let back in** (DL-189 P-04,
   * PE-981). The payment check needs the response's vendors and totals, and the response is dropped
   * on any step back before step 4, so this used to lock Review until step 4 ran again, and a
   * reload on Review landed on step 4. Now a Review already reached, over payments step 4 has
   * confirmed, is reachable, and Review makes the authoritative call itself on entry, then applies
   * the full `isPaymentComplete` check against what comes back: short, and the officer is sent to
   * step 4. So the gate is still the whole payment rule; only *where* it runs has moved.
   */
  const furthestReachableStep = computed(() => {
    const paymentStep = BULK_ORDER_WIZARD_STEPS.indexOf('payment');
    const reviewStep = BULK_ORDER_WIZARD_STEPS.length - 1;

    if (selectedItems.value.length === 0) return 0;
    if (selectedSiteIds.value.length < 2) return 1;
    if (!isAllocationComplete.value) return 2;
    if (!deliveryDate.value) return paymentStep;

    if (lastAuthoritativePreview.value) {
      return isPaymentComplete.value ? reviewStep : paymentStep;
    }

    const hasConfirmedPayments = Object.keys(confirmedOrderKinds.value).length > 0;

    return maxReachedStep.value >= reviewStep && hasConfirmedPayments ? reviewStep : paymentStep;
  });

  /**
   * Set the moment a placement creates anything, so leaving the wizard afterwards is not an exit
   * that discards work: there is none left to discard. `$reset` clears it with everything else.
   */
  const hasPlaced = ref(false);

  /**
   * 🔴 The side-car goes at once, not at `$reset`. Between the two the staged documents upload, and a
   * reload in that window used to restore this wizard at step 4 over orders that already exist,
   * one Place away from a duplicate.
   */
  const markPlaced = (): void => {
    hasPlaced.value = true;
    clearBulkOrderWizardSession();
  };

  /**
   * The step the wizard was on before the current one, so step 4 can tell it was reached from
   * Review (S-11 "Returned from Review"). View state: never persisted.
   */
  const previousStep = ref<number | null>(null);

  const goToStep = (index: number): void => {
    const bounded = Math.max(0, Math.min(index, BULK_ORDER_WIZARD_STEPS.length - 1));

    if (bounded !== currentStep.value) previousStep.value = currentStep.value;

    // Only a step before Payment and delivery can change what the authoritative call priced.
    if (bounded < BULK_ORDER_WIZARD_STEPS.indexOf('payment')) lastAuthoritativePreview.value = null;

    currentStep.value = bounded;
    maxReachedStep.value = Math.max(maxReachedStep.value, bounded);
  };

  const cacheBillItems = (key: string, page: BillItemsPage): void => {
    billItemsCache.value = { ...billItemsCache.value, [key]: page };
  };

  const cacheCatalogueItems = (key: string, page: CatalogueItemsPage): void => {
    catalogueItemsCache.value = { ...catalogueItemsCache.value, [key]: page };
  };

  // ─── Allocate (batch A.3b) ──────────────────────────────────────────────────
  //
  // 🔴 The store computes here, and that reverses what PE-682 and PE-680 were ticketed with.
  // DL-130 settled it: preview is asked twice per order, and the arithmetic between those two
  // calls is this store's. What has NOT changed is the harder half — `remainingBudget`,
  // `remainingQuantity`, `currentLineRate`, `lineTotalBefore`, the variation cause, VAT, the
  // per-vendor split and the date windows are read from the response by path and never derived,
  // and nothing computed here is ever sent back. `POST /bulk-orders` receives the raw grid.

  /** The rate this order pays for an item: the override if set, else the vendor's listed price. */
  const itemRate = (itemId: string): number =>
    rateOverrides.value[itemId] ??
    selectedItems.value.find((item) => item.id === itemId)?.rate ??
    0;

  const previewCellFor = (
    siteId: string,
    itemId: string,
    boqLineItemId?: string | null,
  ): BulkOrderPreviewCell | undefined =>
    previewCells.value[previewCellKey(siteId, itemId, boqLineItemId)];

  /** Every candidate binding the response offers for one `(site, item)`, in response order. */
  const candidateCellsFor = (siteId: string, itemId: string): BulkOrderPreviewCell[] =>
    Object.values(previewCells.value).filter(
      (cell) => cell.siteId === siteId && cell.itemId === itemId && cell.boqLineItemId != null,
    );

  const itemSiteIds = (itemId: string): string[] => Object.keys(allocationGrid.value[itemId] ?? {});

  /**
   * Sections and subsections this order has already invented on one site's bill.
   *
   * 🔴 A section typed into one new line was offered nowhere afterwards, because the picker's
   * options are read off the site's **existing** bill — so inventing "Sitework" once and then
   * adding a second line to it meant typing it again, and a typo there is two sections on the bill
   * where the officer meant one.
   *
   * Derived from the grid rather than kept as its own list, which is what makes it self-maintaining:
   * remove the line that invented a section and the section stops being offered, because nothing on
   * this order is going there any more. Scoped to the **site**, since a bill belongs to a site and
   * not to the item being ordered — a section invented for one item is a real section for the next.
   */
  const proposedPlacementsForSite = (
    siteId: string,
  ): { sections: string[]; subSectionsBySection: Record<string, string[]> } => {
    const drafts = Object.values(allocationGrid.value)
      .flatMap((sites) => sites[siteId] ?? [])
      .map((binding) => binding.proposedLine)
      .filter((line) => line != null);

    // Context: the keys are the officer's own section names, so an ordinary object literal lets
    // one of them land on `Object.prototype` — a section called "constructor" made the `?? []`
    // below read back a function, and the picker then spread a non-iterable (PR #2261 review).
    const subSectionsBySection: Record<string, string[]> = Object.create(null);

    for (const draft of drafts) {
      if (!draft.sectionName || !draft.subSectionName) continue;

      const existing = subSectionsBySection[draft.sectionName] ?? [];
      if (!existing.includes(draft.subSectionName)) {
        subSectionsBySection[draft.sectionName] = [...existing, draft.subSectionName];
      }
    }

    return {
      sections: [...new Set(drafts.map((draft) => draft.sectionName).filter(Boolean))],
      subSectionsBySection,
    };
  };

  /**
   * What is standing between an item and being marked done, as a reason rather than a boolean.
   *
   * 🔴 **"Allocated" is not the same as "finished"**, which is what this corrects: the bar used to
   * offer Mark done the moment any one site had a quantity, so an item could be called done with
   * half its rows blank and a proposed line carrying no specification — a row that fails at
   * placement, discovered three steps later. Each blocker is reported separately because each has a
   * different fix, and the control states which one applies rather than "something is wrong".
   *
   * Order matters: it reports the **first** thing to deal with, working outwards from "nothing
   * here yet" to "one field left".
   */
  const itemMarkDoneBlocker = (
    itemId: string,
  ): {
    kind: 'none' | 'nothing' | 'emptySite' | 'unbound' | 'quantity' | 'specification';
    count: number;
  } => {
    const siteIds = itemSiteIds(itemId);
    if (siteIds.length === 0) return { kind: 'nothing', count: 0 };

    const rows = siteIds.map((siteId) => bindingsFor(itemId, siteId));

    const emptySites = rows.filter((bindings) => bindings.length === 0).length;
    if (emptySites > 0) return { kind: 'emptySite', count: emptySites };

    const all = rows.flat();

    // 🔴 **An unbound row has nowhere for its quantity to land** (PR #2261 review). A picker
    // dismissed without choosing leaves the row bound to nothing, and `allocationRequestRows`
    // drops it — so a quantity typed there counted towards the totals on screen and then went
    // missing from the request. Asked before the quantity because it is the more basic gap.
    const withoutBinding = all.filter(
      (binding) => binding.boqLineItemId == null && binding.proposedLine == null,
    ).length;
    if (withoutBinding > 0) return { kind: 'unbound', count: withoutBinding };

    const withoutQuantity = all.filter((binding) => !isAllocated(binding)).length;
    if (withoutQuantity > 0) return { kind: 'quantity', count: withoutQuantity };

    // A proposed line is persisted at placement, so an empty specification is a real gap rather
    // than a cosmetic one — and this is the last point at which it is cheap to fill.
    const withoutSpecification = all.filter(
      (binding) => binding.proposedLine && !binding.proposedLine.specification.trim(),
    ).length;
    if (withoutSpecification > 0) return { kind: 'specification', count: withoutSpecification };

    return { kind: 'none', count: 0 };
  };

  const bindingsFor = (itemId: string, siteId: string): AllocationBinding[] =>
    allocationGrid.value[itemId]?.[siteId] ?? [];

  /** One row's figures, resolved against its reference cell. */
  const rowFigures = (
    itemId: string,
    siteId: string,
    binding: AllocationBinding,
  ): AllocationRowFigures =>
    rowFiguresFor(
      binding,
      itemRate(itemId),
      previewCellFor(siteId, itemId, binding.boqLineItemId),
      rateTolerance.value,
    );

  /** A site's item total: **the sum of its children**, never a figure of its own (PE-679). */
  const siteTotal = (itemId: string, siteId: string): { quantity: number; total: number } =>
    bindingsFor(itemId, siteId).reduce(
      (running, binding) => {
        const figures = rowFigures(itemId, siteId, binding);

        return {
          quantity: running.quantity + figures.quantity,
          total: running.total + figures.rowTotal,
        };
      },
      { quantity: 0, total: 0 },
    );

  /**
   * One item's rollup.
   *
   * Context: the variation counts are **per site, split on the gating verdict** — a site with any
   * gating child is loud and counts once; a site whose variations are all non-gating is quiet.
   * That split is DL-52's and drives both the completion bar's two lines and the parent-row marker.
   */
  const itemSummary = (itemId: string): ItemAllocationSummary => {
    const summary: ItemAllocationSummary = {
      siteCount: 0,
      allocatedSiteCount: 0,
      totalQuantity: 0,
      total: 0,
      gatingSiteCount: 0,
      nonGatingSiteCount: 0,
    };

    for (const siteId of itemSiteIds(itemId)) {
      summary.siteCount += 1;

      let siteRaises = false;
      let siteGates = false;

      for (const binding of bindingsFor(itemId, siteId)) {
        const figures = rowFigures(itemId, siteId, binding);

        summary.totalQuantity += figures.quantity;
        summary.total += figures.rowTotal;

        if (figures.variation) {
          siteRaises = true;
          if (figures.variation.isGating) siteGates = true;
        }
      }

      if (siteTotal(itemId, siteId).quantity > 0) summary.allocatedSiteCount += 1;
      if (siteGates) summary.gatingSiteCount += 1;
      else if (siteRaises) summary.nonGatingSiteCount += 1;
    }

    return summary;
  };

  /** The order's running total across every item. Announced on change by the summary row. */
  const orderTotal = computed(() =>
    selectedItems.value.reduce((running, item) => running + itemSummary(item.id).total, 0),
  );

  const isItemDone = (itemId: string): boolean => doneItemIds.value.includes(itemId);

  const doneItemCount = computed(
    () => selectedItems.value.filter((item) => isItemDone(item.id)).length,
  );

  /**
   * The step's gate: every item marked done **and still finishable** (PE-678 reports it, PE-668's
   * footer renders it).
   *
   * 🔴 **The done marker is a record of a past decision, not a statement about the grid now** (PR
   * #2261 review). Marking an item done locks nothing — the banner says so — so the officer can
   * clear a quantity or remove a line afterwards and the marker stands. That left Review reachable
   * over rows `allocationRequestRows` had already stopped sending. Re-asking the blocker costs a
   * pass over the grid and makes the gate say what it claims to.
   */
  const isAllocationComplete = computed(
    () =>
      selectedItems.value.length > 0 &&
      selectedItems.value.every(
        (item) => isItemDone(item.id) && itemMarkDoneBlocker(item.id).kind === 'none',
      ),
  );

  const setActiveItem = (itemId: string | null): void => {
    activeItemId.value = itemId;
  };

  /**
   * Marks an item done and advances to the next item that is not.
   *
   * Context: **the advance wraps from the current position**, so finishing the third of five lands
   * on the fourth rather than back at the first. Where none remains the current item stays active —
   * the officer has finished, and jumping them somewhere else would read as an error.
   */
  const markItemDone = (itemId: string): void => {
    if (!isItemDone(itemId)) doneItemIds.value = [...doneItemIds.value, itemId];

    const ids = selectedItems.value.map((item) => item.id);
    const from = ids.indexOf(itemId);

    for (let step = 1; step <= ids.length; step += 1) {
      const candidate = ids[(from + step) % ids.length];
      if (candidate && !isItemDone(candidate)) {
        activeItemId.value = candidate;

        return;
      }
    }
  };

  /** Clears the done state and leaves the item active. Editing a done item never does this. */
  const reopenItem = (itemId: string): void => {
    doneItemIds.value = doneItemIds.value.filter((id) => id !== itemId);
    activeItemId.value = itemId;
  };

  const writeBindings = (itemId: string, siteId: string, bindings: AllocationBinding[]): void => {
    allocationGrid.value = {
      ...allocationGrid.value,
      [itemId]: { ...(allocationGrid.value[itemId] ?? {}), [siteId]: bindings },
    };
  };

  const updateBinding = (
    itemId: string,
    siteId: string,
    bindingId: string,
    change: Partial<AllocationBinding>,
  ): void => {
    writeBindings(
      itemId,
      siteId,
      bindingsFor(itemId, siteId).map((binding) =>
        binding.id === bindingId ? { ...binding, ...change } : binding,
      ),
    );
  };

  /**
   * Writes a typed quantity. **The one value that renders optimistically and issues no request.**
   */
  const setBindingQuantity = (
    itemId: string,
    siteId: string,
    bindingId: string,
    quantity: number | null,
  ): void => updateBinding(itemId, siteId, bindingId, { quantity });

  const setItemRate = (itemId: string, rate: number): void => {
    rateOverrides.value = { ...rateOverrides.value, [itemId]: rate };
  };

  const clearItemRate = (itemId: string): void => {
    const { [itemId]: _dropped, ...rest } = rateOverrides.value;
    rateOverrides.value = rest;
  };

  /**
   * A row pointing at nothing yet — the "New line, no match" state the picker resolves.
   *
   * 🔴 **A real uuid, never a counter, and this was a bug rather than a preference.** The id used
   * to be `${siteId}-b${n}` off a counter held in this store's closure — but **the grid outlives
   * the counter**: `allocationGrid` is restored from the `sessionStorage` side-car on a reload,
   * while `n` restarts at 0. The next Add line on a site that already had one therefore re-minted
   * an id the restored grid still held, and every operation keyed on that id then hit **both**
   * rows: `updateBinding` patched both (so one quantity moved two fields), `removeBinding` filtered
   * both out, and picking a line wrote the same `boqLineItemId` to both — which is what made the
   * second row's line look selectable again in the picker.
   *
   * `ConfirmStep.vue` records the same reasoning for its own rows: an id that outlives the session
   * cannot be derived from anything that does not.
   */
  const emptyBinding = (siteId: string): AllocationBinding => ({
    id: `${siteId}-${crypto.randomUUID()}`,
    boqLineItemId: null,
    boqLineName: null,
    proposedLine: null,
    quantity: null,
  });

  /**
   * Adds a binding, already pointed at whatever the caller chose.
   *
   * Context: Add line used to create an empty row before the picker opened, so cancelling the
   * picker left a "no line selected" row behind (PE-987). It now adds the row only once a line is
   * chosen, in one write, so there is never an unbound row to roll back.
   */
  const addBinding = (
    itemId: string,
    siteId: string,
    initial: Partial<Omit<AllocationBinding, 'id'>> = {},
  ): string => {
    const binding = { ...emptyBinding(siteId), ...initial };

    writeBindings(itemId, siteId, [...bindingsFor(itemId, siteId), binding]);

    return binding.id;
  };

  /**
   * Removes one binding, and **leaves its site standing even when it was the last one**.
   *
   * 🔴 Amended 2026-09-15 (CPTO). It used to take the site off the item with the last line, on the
   * reasoning that S-05 constrains sites per item rather than lines per site, so a site with no
   * line had nothing to say. That stopped being true when the grid stopped auto-matching: a site
   * with no line is now how **every** site arrives, so removing the last one and removing the site
   * became two different intentions with one outcome — and the user who wanted to re-point a line
   * lost the site instead. Taking a site off the item has its own control on the row.
   */
  const removeBinding = (itemId: string, siteId: string, bindingId: string): void => {
    writeBindings(
      itemId,
      siteId,
      bindingsFor(itemId, siteId).filter((binding) => binding.id !== bindingId),
    );
  };

  /**
   * A site joins an item with **no BOQ line on it at all**.
   *
   * 🔴 **This deliberately does not auto-match, and it restores FR-10 rather than overriding it.**
   * The seed used to bind each site to its best tier-1 candidate — an exact vendor-item link off
   * the preview's candidate cells — and the CPTO removed it on 2026-09-15: a line the user did not
   * choose is still a line they have to notice and undo, on up to 100 rows, and which line an
   * order should hit is not something the client can know.
   *
   * ⚠️ **This note used to call that "overriding FR-10's seeding half", and FR-10 has no seeding
   * half.** It reads *"surfaces likely matches at the top — ranked … with the full searchable list
   * below … **Selection is manual**"*: a statement about the picker's **ordering**, ending in the
   * rule the auto-match broke. Requirements' only "pre-selection" is of **sites**, on coverage
   * (§5.2), which the seed still does. So the build had contradicted a requirement and the
   * correction brought it back — worth stating plainly, because a note claiming to override a
   * requirement invites the next reader to restore the behaviour.
   *
   * FR-10's ranking is **not** gone — it is exactly what the picker lists by, which is where a
   * user asking "which line?" is already looking. What is gone is the client answering before
   * being asked.
   */
  const seedSiteBindings = (): AllocationBinding[] => [];

  /**
   * Adds a site to an item, seeded with no quantity.
   *
   * Context: **always a subset of the step-2 selection** — an item is never allocated to a site the
   * officer did not select. The caller offers only selected sites; this is the guard behind it.
   */
  const addSiteToItem = (itemId: string, siteId: string): void => {
    if (!selectedSiteIds.value.includes(siteId)) return;
    if (allocationGrid.value[itemId]?.[siteId]) return;

    writeBindings(itemId, siteId, seedSiteBindings());
  };

  function removeSiteFromItem(itemId: string, siteId: string): void {
    const { [siteId]: _dropped, ...rest } = allocationGrid.value[itemId] ?? {};

    allocationGrid.value = { ...allocationGrid.value, [itemId]: rest };
  }

  /**
   * Drops anything the grid holds that the current selection no longer contains.
   *
   * 🔴 **Nothing else did this** (PR #2261 review). `seedAllocation` is additive by design and
   * prunes nothing, and `removeSiteFromItem` only ever fires from the row control — so going Back,
   * unticking three sites and returning left the grid holding all ten. `orderTotal` iterates
   * `selectedItems` so the figure stayed right, which is what kept it quiet; `allocationRequestRows`
   * iterates the **grid**, so the orphans would have travelled to Review and to placement.
   *
   * Runs before the seed, so the seed sees a clean grid. An item emptied this way keeps its (now
   * empty) entry rather than being deleted, so the rule above still reads it as seeded.
   */
  const reconcileAllocation = (): void => {
    const items = new Set(selectedItems.value.map((item) => item.id));
    const sites = new Set(selectedSiteIds.value);

    allocationGrid.value = Object.fromEntries(
      Object.entries(allocationGrid.value)
        .filter(([itemId]) => items.has(itemId))
        .map(([itemId, bySite]) => [
          itemId,
          Object.fromEntries(Object.entries(bySite).filter(([siteId]) => sites.has(siteId))),
        ]),
    );
  };

  /**
   * Seeds the grid for every selected item that has no rows yet.
   *
   * 🔴 **Additive, never a rebuild.** Re-entering the step — from Back, from a rehydrated session,
   * or from DL-132's return out of Review — must leave every typed quantity, binding and rate
   * override exactly as it was. Only items and sites with no entry at all are seeded, so a second
   * call over a populated grid is a no-op. A return that clears the grid is a defect, not a
   * refresh.
   *
   * `billSiteIds` is the item's bill-carrying sites, read from the Sites step's cached coverage
   * answer rather than refetched — the same read that decided the step-2 selection.
   */
  const seedAllocation = (billSiteIdsByItem: Record<string, string[]>): void => {
    for (const item of selectedItems.value) {
      // 🔴 **Present-but-empty is a decision, not an absence** (PR #2261 review). It used to read
      // `Object.keys(existing).length > 0`, so an officer who took the last site off an item had
      // every bill site handed straight back on the next entry — the additive rule above says
      // exactly that must not happen.
      if (allocationGrid.value[item.id] !== undefined) continue;

      // ⚠️ And an item with no coverage ANSWER is left alone rather than written empty, or a cold
      // cache (a reload straight onto Allocate stores none) would mark it seeded with no sites.
      if (billSiteIdsByItem[item.id] === undefined) continue;

      const seeded = (billSiteIdsByItem[item.id] ?? []).filter((siteId) =>
        selectedSiteIds.value.includes(siteId),
      );

      const rows: Record<string, AllocationBinding[]> = {};
      for (const siteId of seeded) rows[siteId] = seedSiteBindings();

      allocationGrid.value = { ...allocationGrid.value, [item.id]: rows };
    }

    if (
      !activeItemId.value ||
      !selectedItems.value.some((item) => item.id === activeItemId.value)
    ) {
      activeItemId.value = selectedItems.value[0]?.id ?? null;
    }
  };

  /**
   * Folds a preview response into the reference basis.
   *
   * Context: **merged by cell key, not replaced.** An add-sites delta asks only about the pairs the
   * basis does not hold, so its response covers those pairs alone — replacing would drop every
   * site already on screen. `pairs` records what the call actually asked about, so a pair that
   * legitimately came back with no cells is still marked covered and is not re-requested forever.
   */
  const applyPreviewBasis = (
    response: BulkOrderPreviewResponse,
    coveredPairs: { siteId: string; itemId: string }[],
  ): void => {
    const merged = { ...previewCells.value };

    for (const cell of response.cells) {
      merged[previewCellKey(cell.siteId, cell.itemId, cell.boqLineItemId)] = cell;
    }

    previewCells.value = merged;
    rateTolerance.value = response.rateTolerance;

    // Context: one copy, filled in place. The reduce this replaces spread the accumulator per
    // pair, so a 100-site × 50-item basis copied a growing map 5,000 times and blocked the main
    // thread for over a second (PR #2261 review).
    const pairs = { ...previewedPairs.value };
    for (const pair of coveredPairs) pairs[previewCacheKey(pair.siteId, pair.itemId)] = true;

    previewedPairs.value = pairs;
  };

  const hasPreviewBasisFor = (siteId: string, itemId: string): boolean =>
    previewedPairs.value[previewCacheKey(siteId, itemId)] === true;

  const setAuthoritativePreview = (response: BulkOrderPreviewResponse | null): void => {
    lastAuthoritativePreview.value = response;
  };

  const setPausedSites = (siteIds: string[]): void => {
    pausedSiteIds.value = [...new Set(siteIds)];
  };

  const isSitePaused = (siteId: string): boolean => pausedSiteIds.value.includes(siteId);

  /**
   * Why the officer was sent back from Review, and which rows moved (DL-132).
   *
   * 🔴 **This is a flag over the grid, never a replacement for it.** The refreshed figures land in
   * `previewCells` the ordinary way; this only says which rows to mark and how many to name. A
   * return that cleared what was typed would be a defect rather than a refresh, which is why
   * nothing here touches `allocationGrid` or `rateOverrides`.
   */
  /**
   * A `Set` rather than an array, because the lookup below runs per cell.
   *
   * ⚠️ `AllocateStep` asks `isCellStale` across rows × children, so a linear `includes` made it
   * O(rows × movedCells) over two lists that both grow with the grid — the class
   * `.claude/rules/kernel/coding-standards.md` §Performance names explicitly. `movedCount` is held beside it because the
   * banner states a count and a `Set` would otherwise have to be sized on every render.
   */
  /**
   * Rows the placement endpoint refused, keyed exactly as the grid keys its cells.
   *
   * 🔴 **Separate from `staleReturn`, because they say different things.** A stale row's figures
   * moved underneath the officer and its quantity is still valid; a refused row is one the server
   * will not accept as typed, and it carries the server's own words for why. Folding them into one
   * marker would make the banner unable to say which had happened.
   */
  const refusedRows = ref<{ key: string; message: string }[]>([]);

  const setRefusedRows = (rows: { key: string; message: string }[]): void => {
    refusedRows.value = rows;
  };

  const refusedRowMessage = (
    siteId: string,
    itemId: string,
    boqLineItemId?: string | null,
  ): string | undefined =>
    refusedRows.value.find((row) => row.key === previewCellKey(siteId, itemId, boqLineItemId))
      ?.message;

  const staleReturn = ref<{ movedCount: number; cellKeys: Set<string> } | null>(null);

  const setStaleReturn = (cellKeys: string[]): void => {
    staleReturn.value = { movedCount: cellKeys.length, cellKeys: new Set(cellKeys) };
  };

  const clearStaleReturn = (): void => {
    staleReturn.value = null;
  };

  const isCellStale = (siteId: string, itemId: string, boqLineItemId?: string | null): boolean =>
    staleReturn.value?.cellKeys.has(previewCellKey(siteId, itemId, boqLineItemId)) === true;

  /**
   * The grid as the endpoint takes it: a **sparse** list of site-line-quantity rows.
   *
   * Context: a row only where a quantity was entered. The response still answers for every binding
   * the grid could use, so an unallocated pairing needs no row here — and an unbound one has
   * nowhere for its quantity to land, so it is dropped rather than sent as a null line, which the
   * validator refuses.
   *
   * 🔴 **Narrowed to the current selection here, not only by `reconcileAllocation`** (PR #2261
   * review). That reconcile runs when Allocate is entered, and the stepper does not force the
   * officer through it: reaching Review, going Back to Sites, unticking one and clicking Review
   * again never touches it, and the grid still held the dropped site. The reconcile remains — it
   * is what keeps the *screen* honest — and this is what keeps the request honest regardless of
   * the route taken to it.
   */
  const placementAllocationRows = computed(() => {
    const items = new Set(selectedItemIds.value);
    const sites = new Set(selectedSiteIds.value);
    const rows: BulkOrderPlacementAllocation[] = [];

    for (const [itemId, sitesById] of Object.entries(allocationGrid.value)) {
      if (!items.has(itemId)) continue;

      for (const [siteId, bindings] of Object.entries(sitesById)) {
        if (!sites.has(siteId)) continue;

        for (const binding of bindings) {
          const row = requestRowFor(itemId, siteId, binding);
          if (row) rows.push(row);
        }
      }
    }

    return rows;
  });

  /**
   * The same rows as preview takes them.
   *
   * Derived from the placement rows rather than walked a second time, so the two can never disagree
   * about which rows the request covers — only about how much of a proposed line each carries.
   */
  const allocationRequestRows = computed(() => placementAllocationRows.value.map(previewRowFrom));

  /** One rate per item the grid prices, which is the only place a rate is taken from (FR-12). */
  const perItemRateRows = computed(() =>
    selectedItems.value.map((item) => ({ itemId: item.id, rate: itemRate(item.id) })),
  );

  /**
   * Wipes the wizard. `clearAllPiniaStores` (logout) and `clearStoresForOrganisationSwitch`
   * discover this by duck-typing `$reset`, so defining it is what opts the store into them.
   *
   * **It clears the `sessionStorage` side-car too**, and that is the load-bearing part: without it
   * an organisation switch would leave a half-built order sitting in storage, and the next org's
   * wizard would seed itself from another org's project and items on mount. This store is
   * deliberately *not* in the org-switch exclude list for the same reason.
   */
  const $reset = (): void => {
    currentStep.value = 0;
    maxReachedStep.value = 0;
    selectedProjectId.value = null;
    selectedProjectName.value = null;
    selectedItems.value = [];
    selectedSiteIds.value = [];
    seededSitesKey.value = null;
    hasEditedSiteSelection.value = false;
    allocationGrid.value = {};
    rateOverrides.value = {};
    doneItemIds.value = [];
    activeItemId.value = null;
    previewCells.value = {};
    previewedPairs.value = {};
    rateTolerance.value = 0;
    lastAuthoritativePreview.value = null;
    confirmedOrderKinds.value = {};
    previewPending.value = false;
    pausedSiteIds.value = [];
    staleReturn.value = null;
    refusedRows.value = [];
    deliveryDate.value = null;
    payments.value = {};
    documents.value = {};
    previousStep.value = null;
    hasPlaced.value = false;
    billItemsCache.value = {};
    catalogueItemsCache.value = {};
    allocatableSitesCache.value = {};
    siteBoqLinesCache.value = {};

    clearBulkOrderWizardSession();
  };

  return {
    currentStep,
    maxReachedStep,
    furthestReachableStep,
    selectedProjectId,
    selectedProjectName,
    selectedItems,
    selectedItemIds,
    selectedVendors,
    selectedSiteIds,
    seededSitesKey,
    hasEditedSiteSelection,
    allocationGrid,
    rateOverrides,
    doneItemIds,
    activeItemId,
    previewCells,
    previewedPairs,
    rateTolerance,
    lastAuthoritativePreview,
    confirmedOrderKinds,
    confirmOrderKinds,
    previewPending,
    pausedSiteIds,
    staleReturn,
    deliveryDate,
    setDeliveryDate,
    payments,
    paymentFor,
    setPayment,
    documents,
    documentsFor,
    stageDocument,
    unstageDocument,
    isPaymentComplete,
    paymentInformationRows,
    previousStep,
    hasPlaced,
    markPlaced,
    refusedRows,
    setRefusedRows,
    refusedRowMessage,
    setStaleReturn,
    clearStaleReturn,
    isCellStale,
    orderTotal,
    doneItemCount,
    isAllocationComplete,
    allocationRequestRows,
    placementAllocationRows,
    perItemRateRows,
    billItemsCache,
    catalogueItemsCache,
    allocatableSitesCache,
    siteBoqLinesCache,
    isItemSelected,
    addItem,
    removeItem,
    clearItems,
    isSiteSelected,
    toggleSite,
    setSelectedSiteIds,
    clearSites,
    itemProgress,
    sitesProgress,
    discardItem,
    discardSites,
    markSitesSeeded,
    markSitesEdited,
    setProject,
    goToStep,
    cacheBillItems,
    cacheCatalogueItems,
    cacheAllocatableSites,
    cacheSiteBoqLines,
    itemRate,
    previewCellFor,
    candidateCellsFor,
    itemSiteIds,
    proposedPlacementsForSite,
    itemMarkDoneBlocker,
    bindingsFor,
    rowFigures,
    siteTotal,
    itemSummary,
    isItemDone,
    setActiveItem,
    markItemDone,
    reopenItem,
    updateBinding,
    setBindingQuantity,
    setItemRate,
    clearItemRate,
    addBinding,
    removeBinding,
    addSiteToItem,
    removeSiteFromItem,
    reconcileAllocation,
    seedAllocation,
    applyPreviewBasis,
    hasPreviewBasisFor,
    setAuthoritativePreview,
    setPausedSites,
    isSitePaused,
    $reset,
  };
});
